import { useContext, useMemo } from 'react';
import { Column } from 'react-table';
import { Button, Dropdown, MenuProps, Modal, Select, Tag, message } from 'antd';
import * as Icons from '@ant-design/icons';

import { StopPropagation } from '@shared/components/stop-propagation';
import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns';
import { NextTableCheckboxFilter } from '@shared/modules/next-table/components/filters/checkbox';
import { NextTableCheckCell } from '@shared/modules/next-table/components/cells/check';
import { useBackgroundNavigate } from '@shared/hooks';
import { useAuth } from '@modules/me';
import { useStatusesByModelId } from '@modules/statuses';

import { BranchesService } from '../../services';
import { IBranchListItem } from '../../interfaces';
import { BranchesTableContext } from '../../context';
import { branchQueryKeys } from '../../utils';
import { useBranches } from './use-branches';

export const useBranchesTableColumns = (): Column<IBranchListItem>[] => {
  const { handleFetch } = useContext(BranchesTableContext);
  const { can } = useAuth();
  const navigate = useBackgroundNavigate();
  const branches = useBranches();
  const { data: statuses = [] } = useStatusesByModelId(40);

  const actionsColumn = useMemo<Column<IBranchListItem>>(
    () => ({
      ...nextTableColumns.actions,
      Cell: ({ row: { original } }: any) => {
        const items: MenuProps['items'] = [];

        if (can('branch_edit')) {
          items.push({
            key: 'edit',
            label: 'Düzəliş et',
            icon: <Icons.EditOutlined />,
            onClick: () => navigate(`/branches/${original.id}/update`, { withBackground: true }),
          });
        }

        if (can('branch_transfer')) {
          items.push({
            key: 'transfer',
            label: 'Bağla və köçür',
            icon: <Icons.SwapOutlined />,
            onClick: () => navigate(`/branches/${original.id}/transfer`, { withBackground: true }),
          });
        }

        if (can('branch_delete')) {
          if (items.length) items.push({ type: 'divider' });
          items.push({
            key: 'delete',
            label: 'Sil',
            icon: <Icons.DeleteOutlined />,
            danger: true,
            onClick: () => {
              Modal.confirm({
                title: 'Diqqət',
                content: 'Filialı silməyə əminsinizmi?',
                okText: 'Sil',
                cancelText: 'Ləğv et',
                onOk: async () => {
                  const result = await BranchesService.delete(original.id);
                  if (result.status === 200) {
                    message.success('Filial silindi');
                    handleFetch();
                  } else {
                    message.error(result.data as string);
                  }
                },
              });
            },
          });
        }

        return (
          <StopPropagation>
            <Dropdown menu={{ items }} trigger={['hover']}>
              <Button icon={<Icons.MoreOutlined />} size="small" />
            </Dropdown>
          </StopPropagation>
        );
      },
    }),
    [navigate, handleFetch, can],
  );

  const baseColumns = useMemo<Column<IBranchListItem>[]>(
    () => [
      { ...nextTableColumns.small, accessor: (r) => r.id, id: branchQueryKeys.id, Header: 'Kod' },
      { ...nextTableColumns.large, accessor: (r) => r.name, id: branchQueryKeys.name, Header: 'Ad' },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.company?.name,
        id: branchQueryKeys.company.name,
        Header: 'Şirkət adı',
      },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.parent?.name,
        id: branchQueryKeys.parentId,
        Header: 'Üst filial',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
            {branches.data?.map((b) => (
              <Select.Option key={b.id} value={b.id.toString()}>
                {b.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      { accessor: (r) => r.descr, id: branchQueryKeys.descr, Header: 'Açıqlama' },
      {
        ...nextTableColumns.smaller,
        accessor: (r) => r.isBranch,
        id: branchQueryKeys.isBranch,
        Header: 'Filial',
        Cell: NextTableCheckCell,
        Filter: NextTableCheckboxFilter,
      },
      {
        ...nextTableColumns.large,
        accessor: (r) => r.status?.name,
        id: branchQueryKeys.status.id,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }: any) => (
          <Select allowClear={true} style={{ width: '100%' }} onChange={setFilter} value={filterValue}>
            {statuses.map((status) => (
              <Select.Option key={status.id} value={status.id.toString()}>
                {status.name}
              </Select.Option>
            ))}
          </Select>
        ),
        Cell: ({ cell: { value } }: any) => <Tag>{value}</Tag>,
      },
      {
        ...nextTableColumns.date,
        accessor: (r) => r.createdAt,
        id: branchQueryKeys.createdAt,
        Header: 'Yaradılıb',
      },
    ],
    [branches, statuses],
  );

  return useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);
};
